import React, { ChangeEvent, ChangeEventHandler, useCallback, useContext, useRef } from 'react';
import { Box, Collapse, Grid, IconButton, Slider, Stack, styled, TableCell, tableCellClasses, TableRow, Typography } from '@mui/material';
import KeyboardArrowDownIcon from '@mui/icons-material/KeyboardArrowDown';
import KeyboardArrowUpIcon from '@mui/icons-material/KeyboardArrowUp';
import { useState } from 'react';
import { t } from 'i18next';

import { FeatureGeom } from 'features/seismic-3d/models/classes/FeatureGeom';
import { IntersectingLayer } from 'features/seismic-3d/models/types/IntersectingLayer';
import { IntersectingLayerCheckbox } from './IntersectingLayerCheckbox';
import { LayersThreeJsContext } from './LayersThreeJsContext';

export const LayersListRow = ({ layer, checked, minProjectionHeight, onCheck, onUncheck, onUpdate} : LayersListRowProps) => {
    const [ collapsed, setCollapsed ] = useState<boolean>(false);
    const layersThreejs = useContext(LayersThreeJsContext);
    const projHeightRef = useRef(layer.ProjectionHeight ?? 0.01);
    const fillColorRef = useRef(layer.FillColor);
    const fillOpacityRef = useRef(layer.FillOpacity);

    const handleProjectionHeightChange = useCallback((event: Event, value: number | Array<number>) => {
        projHeightRef.current = value as number;
        layersThreejs?.setLayerMeshHeight(layer.Id, value as number);
        setTimeout(() => {
            if (value === projHeightRef.current) {
                onUpdate({ ...layer, ProjectionHeight: value as number });
            }
        }, 500);
    }, [layersThreejs, layer, onUpdate]);

    const handleProjectionOpacityChange = useCallback((event: Event, value: number | number[]) => {
        fillOpacityRef.current = value as number;
        layersThreejs?.changeLayerFillOpacity(layer.Id, value as number);
        setTimeout(() => {
            if (value === fillOpacityRef.current) {
                onUpdate({ ...layer, FillOpacity: value as number });
            }
        }, 500);
    }, [layersThreejs, layer, onUpdate]);

    const handleProjectionColorChange = useCallback<ChangeEventHandler<HTMLInputElement>>((event) => {
        const value = event.target.value;
        fillColorRef.current = value;
        layersThreejs?.changeLayerColor(layer.Id, value);
        setTimeout(() => {
            if (fillColorRef.current === value) {
                onUpdate({ ...layer, FillColor: value });
            }
        }, 500);
    }, [layersThreejs, layer, onUpdate]);

    return (
        <React.Fragment>
            <TableRow sx={{ '& > *': { borderBottom: 'unset' } }}>
                <TableCell size='small'>
                    <IconButton
                        onClick={() => setCollapsed(!collapsed)}
                    >
                        {collapsed ? <KeyboardArrowUpIcon/> : <KeyboardArrowDownIcon/>}
                    </IconButton>
                </TableCell>
                <TableCell size='small'>
                    <IntersectingLayerCheckbox
                        selectCallback={(layerData, featuresGeom) => onCheck(layerData, featuresGeom)}
                        unselectCallback={onUncheck}
                        checked={checked}
                        layerData={layer}
                    />
                </TableCell>
                <TableCell size='small'>
                    {layer.Title}
                </TableCell>
            </TableRow>
            <TableRow>
                <TableCell style={{paddingBottom: 0, paddingTop: 0}} colSpan={3} >
                    <Collapse in={collapsed}>
                        <Box height={'175px'}>
                            <Grid height={'100%'} container spacing={3} sx={{paddingTop: '15px', paddingBottom: '5px', paddingLeft: 0}}>
                                <Grid item xs={7}>
                                    <Stack height={'100%'} paddingTop='0' alignItems='center' justifyContent='flex-end'>
                                        <Stack height={1}>
                                            <Slider
                                                orientation='vertical'
                                                valueLabelDisplay='off'
                                                min={minProjectionHeight}
                                                max={0.01}
                                                step={0.01}
                                                defaultValue={layer.ProjectionHeight ?? 0.01}
                                                onChange={handleProjectionHeightChange}
                                                disabled={minProjectionHeight >= 0 || !checked}
                                            />
                                        </Stack>
                                        <Typography sx={{marginTop: '5px', marginBottom: '0px'}}>{ t('Projection Height').toString() }</Typography>
                                    </Stack>
                                </Grid>
                                <Grid item xs={5} sx={{paddingTop: '-10px', borderLeft: '1px solid', borderColor: 'divider'}}>
                                    <Stack height={'100%'} spacing={2} paddingTop={0}>
                                        <Stack alignItems='start' paddingTop={0}>
                                            <Typography sx={{marginTop: 0}}>
                                                { t('Fill Color').toString() }
                                            </Typography>
                                            <Stack>
                                                <input
                                                    type='color'
                                                    onChange={handleProjectionColorChange}
                                                    defaultValue={layer.FillColor}
                                                    disabled={!checked}
                                                />
                                            </Stack>
                                        </Stack>
                                        <Stack>
                                            <Typography>
                                                Fill Opacity
                                            </Typography>
                                            <Slider
                                                min={0}
                                                max={1}
                                                defaultValue={layer.FillOpacity}
                                                step={0.01}
                                                disabled={!checked}
                                                onChange={handleProjectionOpacityChange}
                                            />
                                        </Stack>
                                    </Stack>
                                </Grid>
                            </Grid>
                        </Box>
                    </Collapse>
                </TableCell>
            </TableRow>
        </React.Fragment>
    );
};

export type LayersListRowProps = {
    layer: IntersectingLayer,
    checked: boolean,
    onCheck: (layerData: IntersectingLayer, featuresGeom: FeatureGeom[]) => void,
    onUncheck: (layerData: IntersectingLayer) => void,
    onUpdate: (layerData: IntersectingLayer) => void,
    minProjectionHeight: number,
    projectionOpacity: number;
};